import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IInspectionInstruction, IInspectionOrder, ISummary } from 'src/app/interfaces/Outputs';
import { Router } from '@angular/router';
import { EInspectionStep } from 'src/app/interfaces/PWA/Inspection';
import { ITourStep } from 'src/app/interfaces/PWA/Tour';
import { TimerService } from '../Timer/timer.service';
import { StepManagerService } from '../StepManager/step-manager.service';
import { InspectionApiService } from '../InspectionApi/inspection-api.service';
import { AlertType } from 'src/app/interfaces/Global/Alert';
import { jwtDecode } from 'jwt-decode';
import { IPayloadToken } from 'src/app/interfaces/Global/PayloadToken';
import { FileService } from '../../General/File/file.service';
import { TourManagerService } from '../TourManager/tour-manager.service';
import { PopupType } from 'src/app/interfaces/PWA/Photo';
import { SectionStepManagerService } from '../SectionStepManager/section-step-manager.service';
import { AppDB } from 'src/app/config/idb';
import { ErrorService } from 'src/app/services/General/Error/error.service'; // added
import { PhotoAnalysisService } from '../photoAnalysis.service';
import { AIDataService } from '../aiData.service';
import { StylesService } from '../../UIHandlers/Styles/styles.service';
import { FormControlService } from '../../General/FormControl/form-control.service';
import { INSPECTION_FINISHED_KEY, markFinishedInThisDocument } from 'src/app/utils/FinishedInspection';

// Utilizar este servicio para mantener persistencia de datos
@Injectable({
  providedIn: 'root',
})
export class InspectionControlService {
  // Steps

  public tourChange$!: Observable<ITourStep[]>;

  private startInFlight: Promise<void> | null = null;

  constructor(
    private readonly inspectionApiService: InspectionApiService,
    private readonly stepManagerService: StepManagerService,
    private readonly sectionStepManagerService: SectionStepManagerService,
    private readonly timerService: TimerService,
    private readonly photoAnalysisService: PhotoAnalysisService,
    private readonly DB: AppDB,
    private readonly tourManagerService: TourManagerService,
    private readonly aIDataService: AIDataService,
    private readonly errorService: ErrorService,
    private readonly stylesService: StylesService,
    private readonly formControlService: FormControlService,
  ) { }

  public async loadInitialInspectionData(
    encrypted_oi: string,
  ): Promise<{
    inspectionOrder?: IInspectionOrder;
    summaryScreen?: ISummary,
    instructions?: IInspectionInstruction[];
  }> {
    try {
      await this.clearDB();
      localStorage.removeItem('photoId');
      const inspectionOrder =
        await this.inspectionApiService.checkInspectionOrder(encrypted_oi);
      const summaryScreen = await this.inspectionApiService.getSummaryScreen(
        encrypted_oi
      );
      const instructions =
        await this.inspectionApiService.getInspectionInstructions(
          encrypted_oi
        );
      await this.stylesService.saveAppTheme(inspectionOrder);
      await this.stylesService.setAppTheme();
      this.formControlService.setHiddenFieldsToStorage(
        inspectionOrder.hidden_elements
      );
      localStorage.setItem('titleError', inspectionOrder.title_error);
      localStorage.setItem(
        'descriptionError',
        inspectionOrder.description_error
      );
      localStorage.setItem(
        'permissionGuideText',
        inspectionOrder.permission_guide_text
      );
      localStorage.setItem('txtCargando', inspectionOrder.txt_cargando);
      await this.DB.addFormSections(inspectionOrder.dynamic_sections);
      return {
        inspectionOrder,
        summaryScreen,
        instructions,
      };
    } catch (error) {
      this.errorService.showErrorScreen();
      return {};
    }
  }


  /**
   * Termina cuando termina la navegación al primer paso, no cuando responde el POST: la espera
   * incluye el resolver de la inspección, que drena la cola y consulta el estado de las fotos.
   * Terminar no garantiza haber entrado: si una guarda o el resolver mandan a otra pantalla, la
   * navegación queda reemplazada y esto resuelve igual.
   *
   * Un segundo llamado mientras corre el primero recibe la misma promesa, no otro POST: un segundo
   * POST emite otro JWT, y su navegación vuelve a correr el resolver. La misma promesa, y no una ya
   * resuelta, porque el segundo llamador tiene que ver el desenlace del primero, también si falla.
   */
  public startInspection(orderData: IInspectionOrder): Promise<void> {
    this.startInFlight ??= this.runStartInspection(orderData).finally(() => {
      this.startInFlight = null;
    });
    return this.startInFlight;
  }

  private async runStartInspection(orderData: IInspectionOrder): Promise<void> {
    try {
      const steps = orderData.steps.filter((step) => step.id !== 1 && step.id !== 2);
      // Antes del POST: sin un paso navegable no hay adónde entrar. La navegación resolvería sin
      // salir de esta página, el botón quedaría en «Iniciando...» para siempre y habría un JWT
      // emitido que nadie usa.
      if (steps.length === 0) {
        throw new Error('La orden de inspección no trae pasos navegables.');
      }
      // Ni la orden ni el payload del JWT van a consola: traen RUT, nombre, email y teléfono, y
      // Sentry graba la consola como breadcrumb, con los argumentos crudos.
      const { jwt } = await this.inspectionApiService.startInspectionOrder(
        orderData
      );
      // Sin asignar a propósito: un JWT mal formado corta el inicio acá, antes de guardarse.
      jwtDecode<IPayloadToken>(jwt);
      localStorage.setItem('jwt', jwt);
      localStorage.setItem('type', orderData.type);
      this.setTotalPhotos(orderData.total_photos);
      this.stepManagerService.saveSteps(steps);
      this.sectionStepManagerService.saveSectionSteps(
        orderData.dynamic_sections
      );
      this.tourManagerService.saveAppTooltipsTargets(orderData);
      // this.photoAnalysisService.subscribeToPhotoAnalysis(jwt);
      localStorage.setItem(INSPECTION_FINISHED_KEY, 'false');
      this.aIDataService.setAITexts(orderData.ai_texts);
      this.aIDataService.setHasAIAvailable(orderData.SWITCH_IA);
      // this.aIDataService.setHasAIAvailable(true);
      // this.getInspectionProgress();

      await this.stepManagerService.navigateToFirstStep();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async initInspectionTimer() {
    // Con la inspección cerrada no hay nada que vencer: el vencimiento solo mandaría a la pantalla de error a
    // quien recargó la evaluación.
    if (this.isInspectionFinished()) {
      return;
    }
    const jwt = localStorage.getItem('jwt');
    if (!jwt) {
      throw new Error('No JWT found');
    }
    const remainingTime = this.timerService.getRemainingTimeFromToken(jwt);
    // const remainingTime = 10000;
    if (remainingTime <= 0) {
      await this.handleInspectionExpired();
      return;
    }
    this.timerService.setTimer(
      remainingTime,
      this.handleInspectionExpired.bind(this)
    );
  }

  public async isInspectionActive(): Promise<boolean> {
    const jwt = localStorage.getItem('jwt');
    if (!jwt) {
      return false;
    }
    const remainingTime = this.timerService.getRemainingTimeFromToken(jwt);
    return remainingTime > 0;
  }

  public async handleInspectionExpired() {
    await this.clearInspectionData();
    this.errorService.showErrorScreen();
  }

  public async clearInspectionData(): Promise<void> {
    // Sin `if (jwt)`: desuscribirse no depende del token, y con la guarda anterior una inspección
    // cuyo JWT ya se había limpiado dejaba la conexión abierta.
    this.photoAnalysisService.unsubscribeFromPhotoAnalysis();
    // Con `await`: sin él la promesa quedaba suelta y quien esperaba a `clearInspectionData`
    // seguía con el borrado a medio hacer, así que la pantalla de error podía montarse sobre una
    // base todavía viva.
    await this.clearDB();
    this.timerService.stopTimer();
  }

  public async clearDB() {
    localStorage.clear();
    await this.DB.clearDB();
  }

  /**
   * Solo con el 200 del cierre (`finish.page.ts`). Desde acá no hay veredictos que esperar, y la conexión de
   * Pusher se cierra: abierta, reconectaría con el JWT ya vencido y `push/auth` respondería 401.
   */
  public setInspectionAsFinished(): void {
    localStorage.setItem(INSPECTION_FINISHED_KEY, 'true');
    markFinishedInThisDocument();
    this.timerService.stopTimer();
    this.photoAnalysisService.unsubscribeFromPhotoAnalysis();
  }

  public isInspectionFinished(): boolean {
    return localStorage.getItem(INSPECTION_FINISHED_KEY) === 'true';
  }

  public isFixInspection(): boolean {
    const isFix = localStorage.getItem('type') === 'Fix';
    return isFix;
  }

  private setTotalPhotos(totalPhotos: number): void {
    localStorage.setItem('total_photos', totalPhotos.toString());
  }
}
